/**
 * Consumer wallet shell — reorganised around consumer LIFE, not institutional
 * functions.
 *
 *   Home · My Documents · My Trusted Institutions ·
 *   My Consumer Record · My Digital Safety · Life Journeys · Profile
 */
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Home, Wallet, FolderOpen, Building2, UserSquare2, ShieldCheck,
  Compass, ClipboardList, FlaskConical, Settings as SettingsIcon, Menu,
  LineChart, UserCog, Info, Gavel,
} from "lucide-react";
import NaviBubble from "@/components/wallet/NaviBubble";
import NaviFace from "@/components/wallet/NaviFace";
import NaviIntroModal from "@/components/wallet/NaviIntroModal";
import InstallAppPrompt from "@/components/wallet/InstallAppPrompt";
import ConsentPendingChip from "@/components/wallet/ConsentPendingChip";
import LanguageSwitcher from "@/components/wallet/LanguageSwitcher";
import { T, useT } from "@/contexts/TranslationContext";
import { pingWalletOpen } from "@/lib/wallet-signal";
import { useDropOffPolling } from "@/lib/drop-off-notifications";

export const NAVY = "#0A1F44";
export const NAVY_DARK = "#061633";
export const GOLD = "#C9A24A";
export const BG = "#F8F9FA";

const NAV = [
  { key: "home",         to: "/wallet",              label: "Home",                  short: "Home",       icon: Home },
  { key: "profile",      to: "/wallet/profile",      label: "Financial Profile",     short: "Profile",    icon: UserCog },
  { key: "decisions",    to: "/wallet/decisions",    label: "My Decisions",          short: "Decisions",  icon: Gavel },
  { key: "documents",    to: "/wallet/documents",    label: "My Financial Documents", short: "Documents",  icon: FolderOpen },
  { key: "institutions", to: "/wallet/institutions", label: "My Trusted Institutions", short: "Institutions", icon: Building2 },
  { key: "record",       to: "/wallet/record",       label: "My Consumer Record",    short: "Record",     icon: UserSquare2 },
  { key: "safety",       to: "/wallet/safety",       label: "Trust & Safety",        short: "Trust",      icon: ShieldCheck },
  { key: "journeys",     to: "/wallet/life-journeys", label: "Life Journeys",        short: "Journeys",   icon: Compass },
  { key: "investments",  to: "/wallet/investments",  label: "Investments",           short: "Invest",     icon: LineChart },
  { key: "assistance",   to: "/wallet/assistance",   label: "My Assistance Requests", short: "Assistance", icon: ClipboardList },
  { key: "about",        to: "/wallet/about",        label: "About",                 short: "About",      icon: Info,           section: "about" },
  { key: "demos",        to: "/wallet/demos",        label: "Demo Simulation",       short: "Demos",      icon: FlaskConical,   section: "internal" },
];

const SECTION_LABELS = {
  about:    "About your wallet",
  internal: "Internal",
};

export function WalletShell({ children, activeTab }) {
  useEffect(() => {
    pingWalletOpen({ referrer_kind: window.location.pathname.startsWith("/j/") ? "banker_push" : "direct" });
  }, []);
  useDropOffPolling();
  return (
    <div className="min-h-screen flex" style={{ background: BG }}>
      {/* ─── Desktop side rail ─── */}
      <aside className="hidden lg:flex flex-col w-72 border-r border-slate-100 bg-white sticky top-0 h-screen">
        <Link to="/wallet" className="flex items-center gap-2.5 px-6 py-5 border-b border-slate-100">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center font-display font-bold text-white text-lg" style={{ background: NAVY }}>A</div>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold" style={{ color: NAVY }}>Altara</div>
            <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Trust Wallet</div>
          </div>
        </Link>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map((item, i) => {
            const prevSection = i > 0 ? (NAV[i - 1].section || "consumer") : null;
            const thisSection = item.section || "consumer";
            const showDivider = prevSection && prevSection !== thisSection;
            const sectionLabel = SECTION_LABELS[thisSection] || thisSection;
            return (
              <div key={item.key}>
                {showDivider && (
                  <div className="pt-3 pb-1 px-3">
                    <div className="text-[9px] uppercase tracking-[0.24em] text-slate-400 font-semibold"><T>{sectionLabel}</T></div>
                  </div>
                )}
                <SideNavItem item={item} active={activeTab === item.key} />
              </div>
            );
          })}
        </nav>
        <div className="border-t border-slate-100 p-3 space-y-1">
          <LanguageSwitcher variant="sidebar" />
          <Link
            to="/wallet/settings"
            data-testid="wallet-profile-link"
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition"
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
              <SettingsIcon className="w-3.5 h-3.5" style={{ color: NAVY }} />
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold truncate" style={{ color: NAVY }}><T>Profile & settings</T></div>
              <div className="text-[11px] text-slate-500 truncate"><T>Language · accessibility · account</T></div>
            </div>
          </Link>
        </div>
      </aside>

      {/* ─── Main column ─── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top header */}
        <header className="lg:hidden bg-white border-b border-slate-100 sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 py-3">
            <Link to="/wallet" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center font-display font-bold text-white" style={{ background: NAVY }}>A</div>
              <div className="leading-tight">
                <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Altara</div>
                <div className="text-[9px] uppercase tracking-[0.2em]" style={{ color: GOLD }}>Trust Wallet</div>
              </div>
            </Link>
            <div className="flex items-center gap-1">
              <div className="w-44"><LanguageSwitcher variant="mobile-header" /></div>
              <Link to="/wallet/settings" data-testid="wallet-settings-link" className="p-1.5 rounded-lg hover:bg-slate-50" title="Settings">
                <SettingsIcon className="w-4 h-4 text-slate-500" />
              </Link>
            </div>
          </div>
        </header>

        <ConsentPendingChip />

        <main className="flex-1 pb-24 lg:pb-12">{children}</main>
      </div>

      <NaviBubble />
      <NaviIntroModal />
      <InstallAppPrompt />

      {/* ─── Mobile bottom-nav (Home · My Records · NAVI · Decisions · More) ─── */}
      <MobileBottomNav activeTab={activeTab} />
    </div>
  );
}

/**
 * Mobile bottom navigation.
 *
 * Five slots. NAVI sits dead-centre as an elevated pill (bigger, gold-
 * ringed portrait) that opens the floating chat panel. "More" opens a
 * drawer with every remaining nav destination.
 */
function MobileBottomNav({ activeTab }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const items = [
    NAV.find(n => n.key === "home"),
    { key: "records",   to: "/wallet/documents", label: "My Records", short: "Records",   icon: FolderOpen },
    { key: "navi",      isNavi: true },
    { key: "decisions", to: "/wallet/decisions", label: "Decisions",   short: "Decisions", icon: Gavel },
    { key: "more",      isMore: true },
  ];
  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-20 lg:hidden">
        <div className="max-w-2xl mx-auto grid grid-cols-5 items-end px-2 pt-1 pb-1.5">
          {items.map((it) => {
            if (it.isNavi) {
              const naviActive = false;
              return (
                <button
                  key="navi"
                  onClick={() => window.dispatchEvent(new CustomEvent("navi.open"))}
                  data-testid="mobile-nav-navi"
                  aria-label="Ask NAVI"
                  className="relative -mt-6 flex flex-col items-center justify-end"
                >
                  <span className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg" style={{ background: NAVY, boxShadow: `0 6px 18px rgba(10,31,68,0.35)` }}>
                    <NaviFace size="md" ring={false} />
                  </span>
                  <span className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: NAVY }}><T>NAVI</T></span>
                  {naviActive && <span className="absolute -bottom-1 w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />}
                </button>
              );
            }
            if (it.isMore) {
              const isActive = moreOpen;
              return (
                <button
                  key="more"
                  onClick={() => setMoreOpen(true)}
                  data-testid="mobile-nav-more"
                  aria-label="More"
                  className="flex flex-col items-center gap-0.5 py-2.5 text-[10px] uppercase tracking-widest"
                  style={{ color: isActive ? NAVY : "#94A3B8" }}
                >
                  <Menu className="w-4 h-4" strokeWidth={isActive ? 2.4 : 1.8} />
                  <T>More</T>
                </button>
              );
            }
            const active = activeTab === it.key || (it.key === "records" && activeTab === "documents");
            return <TabItem key={it.key} item={it} active={active} />;
          })}
        </div>
      </nav>

      {/* More drawer */}
      {moreOpen && <MoreDrawer activeTab={activeTab} onClose={() => setMoreOpen(false)} />}
    </>
  );
}

function MoreDrawer({ activeTab, onClose }) {
  // Show every remaining destination the primary bar doesn't already surface.
  const primaryKeys = new Set(["home", "documents", "record"]);
  const remaining = NAV.filter(n => !primaryKeys.has(n.key));
  return (
    <div
      data-testid="mobile-more-drawer"
      className="fixed inset-0 z-30 lg:hidden"
      style={{ background: "rgba(6,22,51,0.55)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl max-h-[75vh] overflow-y-auto p-3 pb-8">
        <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-3" />
        {remaining.map((item) => {
          const Icon = item.icon;
          const active = activeTab === item.key;
          return (
            <Link
              key={item.key}
              to={item.to}
              onClick={onClose}
              data-testid={`more-drawer-${item.key}`}
              className="flex items-center gap-3 py-3 px-3 rounded-xl"
              style={{ background: active ? `${GOLD}22` : "transparent" }}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: active ? NAVY : "#F1F5F9" }}>
                <Icon className="w-4 h-4" style={{ color: active ? "white" : NAVY }} />
              </div>
              <span className="text-[14px] font-medium" style={{ color: NAVY }}><T>{item.label}</T></span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function SideNavItem({ item, active }) {
  const Icon = item.icon;
  const isChild = Boolean(item.parent);
  return (
    <Link
      to={item.to}
      data-testid={`sidenav-${item.key}`}
      className={`flex items-center gap-3 py-2.5 rounded-xl transition group ${isChild ? "pl-9 pr-3" : "px-3"}`}
      style={{
        background: active ? NAVY : "transparent",
        color: active ? "white" : "#334155",
      }}
    >
      <Icon className={`${isChild ? "w-3.5 h-3.5" : "w-4 h-4"} flex-shrink-0`} strokeWidth={active ? 2.2 : 1.8} />
      <span className={`${isChild ? "text-[12px]" : "text-[13px]"} font-medium truncate`}><T>{item.label}</T></span>
      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />}
    </Link>
  );
}

function TabItem({ item, active }) {
  const Icon = item.icon;
  return (
    <Link to={item.to} data-testid={`tab-${item.key}`} className="flex flex-col items-center gap-0.5 py-2.5 text-[10px] uppercase tracking-widest" style={{ color: active ? NAVY : "#94A3B8" }}>
      <Icon className="w-4 h-4" strokeWidth={active ? 2.4 : 1.8} />
      <T>{item.short}</T>
    </Link>
  );
}

export function useWalletActive(loc = useLocation()) {  // eslint-disable-line react-hooks/rules-of-hooks
  const p = loc.pathname;
  if (p.startsWith("/wallet/profile"))        return "profile";
  if (p.startsWith("/wallet/about"))          return "about";
  if (p.startsWith("/wallet/documents"))      return "documents";
  if (p.startsWith("/wallet/documents"))      return "documents";
  if (p.startsWith("/wallet/institutions"))   return "institutions";
  if (p.startsWith("/wallet/record"))         return "record";
  if (p.startsWith("/wallet/safety"))         return "safety";
  if (p.startsWith("/wallet/life-journeys"))  return "journeys";
  if (p.startsWith("/wallet/investments"))    return "investments";
  if (p.startsWith("/wallet/scam"))           return "safety";  // legacy alias
  if (p.startsWith("/wallet/product-reviews")) return "home";
  if (p.startsWith("/wallet/learning"))       return "home";
  if (p.startsWith("/wallet/decisions"))      return "decisions";
  if (p.startsWith("/wallet/glossary"))       return "documents";
  if (p.startsWith("/wallet/passport"))       return "profile";  // legacy — Passport menu retired; keep deep-links active but light Profile
  if (p.startsWith("/j/"))                    return "home";
  return "home";
}
